import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { getQueryBuilderModel } from 'src/containers/ProjectDiagram/utils/getQueryBuilderModel';
import {
  ModelInfo,
  ModelsInfo,
  RulesInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { Database } from 'src/store/state/databases';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { exportRules } from 'src/utils/export/rules';
import { ExportToFileOptions } from 'src/utils/exportToFile';

export const publishRules = async ({
  database,
  isConsolidatingModels,
  modelsInfo,
  project,
  publishedModelNames,
  ruleInfo,
}: {
  database: Database;
  isConsolidatingModels: boolean;
  modelsInfo: ModelsInfo;
  project: Project;
  publishedModelNames: Set<string>;
  ruleInfo: RulesInfo;
}): Promise<PublishProjectResponse[]> => {
  if (
    !Object.keys(ruleInfo).length ||
    (isConsolidatingModels && !modelsInfo[project.targetModelId])
  ) {
    return [];
  }

  // Publish the rules
  const modelInfosForRules = isConsolidatingModels
    ? [modelsInfo[project.targetModelId]]
    : Object.values(modelsInfo).filter(
        (modelInfo) =>
          modelInfo.shouldPublish &&
          publishedModelNames.has(modelInfo.modelName)
      );
  const ruleModel = getQueryBuilderModel(project.diagram.model);

  const databaseId = database.id;

  const ruleResponses = modelInfosForRules.flatMap((modelInfo) => {
    const rulesInfoForCurrentModel = isConsolidatingModels
      ? ruleInfo
      : Object.values(ruleInfo).reduce<RulesInfo>((acc, rule) => {
          if (rule.models.some((model) => modelInfo.projectModelId === model)) {
            acc[rule.id] = rule;
          }
          return acc;
        }, {});

    const rulesFile = exportRules(
      project.name,
      ruleModel,
      rulesInfoForCurrentModel
    );
    if (!rulesFile) {
      return [];
    }

    return [
      publishRule({
        databaseId,
        modelInfo,
        rulesFile,
      }),
    ];
  });

  return Promise.all(ruleResponses);
};

export const publishRule = async ({
  databaseId,
  modelInfo,
  rulesFile,
}: {
  databaseId: string;
  modelInfo: ModelInfo;
  rulesFile: ExportToFileOptions;
}): Promise<PublishProjectResponse> => {
  // Begin a transaction
  const transactionResponse =
    await databasesStardogRequestDispatchers.beginDatabaseTransaction(
      databaseId
    );

  const transactionId = transactionResponse.payload.response?.body;

  if (
    transactionResponse.type ===
      DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE ||
    !transactionId
  ) {
    return {
      success: false,
      action: transactionResponse,
      actionDescription: copy.notifications.database.beginTransaction,
      message: copy.notifications.publishProject.rules.failure(
        modelInfo.modelName
      ),
    };
  }

  // Prep to rollback if we need to
  const rollback = async () => {
    await databasesStardogRequestDispatchers.rollbackDatabaseTransaction(
      databaseId,
      transactionId
    );
  };

  // Publish the rules
  const addDataResponse =
    await databasesStardogRequestDispatchers.addDataToDatabase({
      databaseId,
      transactionId,
      contents: rulesFile.contents,
      mimeType: rulesFile.mimeType,
      namedGraph: modelInfo.namedGraphIri,
    });

  if (
    addDataResponse.type === DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
  ) {
    // Stardog doesn't actually start a transaction until an action
    // has succeeded, so no need to rollback the first one if it fails
    return {
      success: false,
      action: addDataResponse,
      actionDescription: copy.notifications.database.addData,
      message: copy.notifications.publishProject.rules.failure(
        modelInfo.modelName
      ),
    };
  }

  // Commit the transaction
  const commitResponse =
    await databasesStardogRequestDispatchers.commitDatabaseTransaction(
      databaseId,
      transactionId
    );

  if (
    commitResponse.type ===
    DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE
  ) {
    await rollback();
    return {
      success: false,
      action: commitResponse,
      actionDescription: copy.notifications.database.commitTransaction,
      message: copy.notifications.publishProject.rules.failure(
        modelInfo.modelName
      ),
    };
  }

  return {
    success: true,
    action: commitResponse,
    message: copy.notifications.publishProject.rules.success(
      modelInfo.modelName
    ),
  };
};
